import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Target, GraduationCap, ClipboardCheck, Wallet,
  Boxes, FileCheck, BookOpenText, Award, Briefcase, MessageSquareText,
  SlidersHorizontal, Users, Building2, UserCog, LogOut,
  Activity, Clock, MonitorPlay, UserCheck, FileBarChart2,
  CalendarClock, Receipt, AlertCircle,
  BookOpen, ClipboardList, SquareCheckBig,
  ClipboardPen, BarChart3, FolderTree, Building, Handshake,
  Star, MessageSquareQuote, ListChecks, GitMerge, DoorOpen, CalendarCheck,
  UserRound, UserRoundX, BadgeInfo, Network, MapPin, KeyRound, Grid2x2, BadgeCheck,
  ShieldCheck, ShieldEllipsis, KeySquare, UsersRound, TableProperties, ScrollText, Mails,
  ChevronRight, UserCircle2, CreditCard, Library, Layers, CircleHelp, Tag,
} from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { canAccessOrganizationList } from "../../Utils/organizationPermissions";
import { getLoginPathAfterLogout } from "../../Utils/studentPortalPath";
import { TRAINER_PORTAL_ROUTES } from "../../Utils/trainerPortal";
import {
  resolveActiveWorkspace,
  WORKSPACES,
} from "../../Utils/workspaces";

/* ── Section label map ───────────────────────── */
const SECTION_BEFORE = {
  Dashboard:           "Overview",
  Leads:               "Engagement",
  Fees:                "Operations",
  Certificates:        "Outcomes",
  "Employee Management": "Administration",
  "Organization Setup":  "Configuration",
  "My Profile":          "Student",
};

const STUDENT_OPTIONS = [
  {
    heading: "Dashboard",
    title: "Dashboard",
    route: "/student/dashboard",
    icon: <LayoutDashboard size={19} strokeWidth={2} />,
  },
  {
    heading: "My Courses",
    title: "My Courses",
    route: "/student/courses",
    icon: <BookOpen size={19} strokeWidth={2} />,
    PermissionKey: "VIEW_SELF_STUDENT_COURSES",
  },
  {
    heading: "My Schedule",
    title: "My Schedule",
    route: "/student/class-records",
    icon: <ClipboardList size={19} strokeWidth={2} />,
  },
  {
    heading: "My Attendance",
    title: "My Attendance",
    route: "/student/attendance",
    icon: <SquareCheckBig size={19} strokeWidth={2} />,
  },
  {
    heading: "My Assessments",
    title: "My Assessments",
    route: "/student/assignments",
    icon: <ClipboardPen size={19} strokeWidth={2} />,
  },
  {
    heading: "My Fees & Payments",
    title: "My Fees & Payments",
    route: "/student/fees",
    icon: <CreditCard size={19} strokeWidth={2} />,
    PermissionKey: "VIEW_SELF_STUDENT_FEES",
  },
  {
    heading: "Explore Courses",
    title: "Explore Courses",
    route: "/student/catalog",
    icon: <Library size={19} strokeWidth={2} />,
    PermissionKey: "VIEW_SELF_STUDENT_CATALOG",
  },
  {
    heading: "My Profile",
    title: "My Profile",
    route: "/student/profile",
    icon: <UserCircle2 size={19} strokeWidth={2} />,
    PermissionKey: "VIEW_SELF_STUDENT_PROFILE",
  },
];

const PLACEMENT_AGENCY_PERMISSIONS =
  "VIEW_RECRUITMENT_AGENCIES||ADD_RECRUITMENT_AGENCIES||EDIT_RECRUITMENT_AGENCIES||DELETE_RECRUITMENT_AGENCIES";
const PLACEMENT_COMPANY_PERMISSIONS =
  "VIEW_COMPANIES||ADD_COMPANIES||EDIT_COMPANIES||DELETE_COMPANIES";
const PLACEMENT_REFERRAL_PERMISSIONS =
  "VIEW_PLACEMENT_REFERRALS||ADD_PLACEMENT_REFERRAL||EDIT_PLACEMENT_REFERRAL||DELETE_PLACEMENT_REFERRAL";
const PLACEMENT_ASSISTANCE_PERMISSIONS = "VIEW_PLACEMENT_ASSISTANCE";
const PLACEMENT_MOCK_INTERVIEW_PERMISSIONS = "VIEW_PLACEMENT_MOCK_INTERVIEWS";
const PLACEMENT_ANY_PERMISSIONS = [
  PLACEMENT_AGENCY_PERMISSIONS,
  PLACEMENT_COMPANY_PERMISSIONS,
  PLACEMENT_REFERRAL_PERMISSIONS,
].join("||");

/* ── Sidebar nav options ─────────────────────── */
const BASE_OPTIONS = [
  {
    heading: "Dashboard",
    title: "Dashboard",
    route: "/dashboard",
    icon: <LayoutDashboard size={19} strokeWidth={2} />,
  },
  {
    heading: "Leads",
    title: "Leads",
    icon: <Target size={19} strokeWidth={2} />,
    children: [
      { route: "/leads",                        title: "Leads",                  icon: <UsersRound size={16} strokeWidth={2} />,     PermissionKey: "VIEW_LEADS||VIEW_ASSIGNED_LEADS" },
      { route: "/lead-activities",              title: "Lead Activities",        icon: <Activity size={16} strokeWidth={2} />,      PermissionKey: "VIEW_LEAD_ACTIVITY||VIEW_ASSIGNED_LEAD_ACTIVITY" },
      { route: "/lead-activity-report",         title: "Lead Activity Report",   icon: <FileBarChart2 size={16} strokeWidth={2} />, PermissionKey: "VIEW_LEAD_ACTIVITY_REPORT" },
      { route: "/counsellor-report",            title: "Counsellor report",      icon: <FileBarChart2 size={16} strokeWidth={2} />, PermissionKey: "VIEW_COUNSELLOR_REPORT" },
      { route: "/lead-contact-timings",         title: "Lead Contact Timings",   icon: <Clock size={16} strokeWidth={2} />,         PermissionKey: "VIEW_LEAD_CONTACT_TIMINGS" },
      { route: "/demo-sessions",                title: "Demo Sessions",          icon: <MonitorPlay size={16} strokeWidth={2} />,   PermissionKey: "VIEW_DEMO_SESSIONS" },
      { route: "/demo-participants",            title: "Demo Participants",      icon: <UserCheck size={16} strokeWidth={2} />,     PermissionKey: "VIEW_DEMO_PARTICIPANTS" },
      { route: "/leads-monthly-funnel-report",  title: "Lead Monthly Report",    icon: <FileBarChart2 size={16} strokeWidth={2} />, PermissionKey: "LEAD_MONTHLY_REPORT" },
    ],
  },
  {
    heading: "Students",
    title: "Students",
    route: "/students",
    icon: <GraduationCap size={19} strokeWidth={2} />,
    PermissionKey: "VIEW_STUDENTS",
  },
  {
    heading: "Admissions",
    title: "Admissions",
    route: "/admissions",
    icon: <ClipboardCheck size={19} strokeWidth={2} />,
    PermissionKey: "VIEW_ADMISSIONS",
  },
  {
    heading: "Offers",
    title: "Offers",
    icon: <Tag size={19} strokeWidth={2} />,
    children: [
      { route: "/offers", title: "Offers", icon: <Tag size={16} strokeWidth={2} />, PermissionKey: "VIEW_OFFERS" },
      { route: "/offer-audiences", title: "Offer Audiences", icon: <UsersRound size={16} strokeWidth={2} />, PermissionKey: "VIEW_OFFER_AUDIENCES" },
      { route: "/offer-redemptions", title: "Redemptions", icon: <FileBarChart2 size={16} strokeWidth={2} />, PermissionKey: "VIEW_OFFER_REDEMPTIONS" },
    ],
  },
  {
    heading: "Fees",
    title: "Fees",
    icon: <Wallet size={19} strokeWidth={2} />,
    children: [
      { route: "/fee-installments", title: "Fee Installments", icon: <CalendarClock size={16} strokeWidth={2} />, PermissionKey: "VIEW_FEE_INSTALLMENTS||VIEW_ASSIGNED_STUDENT_FEE" },
      { route: "/students-fees",    title: "Fee Receipts",     icon: <Receipt size={16} strokeWidth={2} />,       PermissionKey: "VIEW_STUDENT_FEE||VIEW_ASSIGNED_STUDENT_FEE" },
      { route: "/fee-due-report",   title: "Fee Due",          icon: <AlertCircle size={16} strokeWidth={2} />,   PermissionKey: "FEE_DUE_REPORT" },
    ],
  },
  {
    heading: "Batches",
    title: "Batches",
    icon: <Boxes size={19} strokeWidth={2} />,
    children: [
      { route: "/batches",                  title: "Batches",                  icon: <Boxes size={16} strokeWidth={2} />,        PermissionKey: "VIEW_BATCHES" },
      { route: "/batch-modules",            title: "Batch Modules",            icon: <Layers size={16} strokeWidth={2} />,       PermissionKey: "VIEW_BATCHES" },
      { route: "/batch-trainers",           title: "Batch Trainers",           icon: <UserCheck size={16} strokeWidth={2} />,    PermissionKey: "VIEW_BATCHES" },
      { route: "/batch-trainer-modules",    title: "Trainer Modules",          icon: <UserCheck size={16} strokeWidth={2} />,    PermissionKey: "VIEW_BATCHES" },
      { route: "/batch-students",           title: "Batch Students",           icon: <Users size={16} strokeWidth={2} />,        PermissionKey: "VIEW_BATCH_STUDENTS" },
      { route: "/batch-classes",            title: "Batch Classes",            icon: <BookOpen size={16} strokeWidth={2} />,     PermissionKey: "VIEW_BATCH_CLASS" },
      { route: "/batch-class-trainers",     title: "Batch Class Trainers",           icon: <UserCheck size={16} strokeWidth={2} />,    PermissionKey: "VIEW_BATCH_CLASS" },
      { route: "/class-records",            title: "Batch Class Records",      icon: <ClipboardList size={16} strokeWidth={2} />,PermissionKey: "VIEW_BATCH_CLASS_RECORD" },
      { route: "/batch-class-record-trainers", title: "Batch Class Record Trainers",      icon: <UserCheck size={16} strokeWidth={2} />,    PermissionKey: "VIEW_BATCH_CLASS_RECORD" },
      { route: "/classes-attendance",       title: "Attendance",               icon: <SquareCheckBig size={16} strokeWidth={2} />, PermissionKey: "VIEW_CLASS_ATTENDANCES" },
      { route: "/attendance-report",        title: "Attendance Report",        icon: <FileBarChart2 size={16} strokeWidth={2} />, PermissionKey: "VIEW_CLASS_ATTENDANCES" },
    ],
  },
  {
    heading: "Assesments",
    title: "Assessments",
    icon: <FileCheck size={19} strokeWidth={2} />,
    children: [
      { route: "/assessment-types",     title: "Assessment Types",   icon: <ListChecks size={16} strokeWidth={2} />, PermissionKey: "VIEW_ASSESSMENT_TYPES" },
      { route: "/assesments",        title: "Assessments",       icon: <ClipboardPen size={16} strokeWidth={2} />,  PermissionKey: "VIEW_ASSESSMENTS" },
      { route: "/assessment-tests",     title: "Tests",              icon: <CircleHelp size={16} strokeWidth={2} />, PermissionKey: "VIEW_ASSESSMENT_TESTS" },
      { route: "/assessment-schedules", title: "Schedules",          icon: <CalendarClock size={16} strokeWidth={2} />, PermissionKey: "VIEW_ASSESSMENT_SCHEDULES" },
      { route: "/assesment-results", title: "Assessment Results", icon: <BarChart3 size={16} strokeWidth={2} />,    PermissionKey: "VIEW_ASSESSMENT_RESULTS" },
    ],
  },
  {
    heading: "Trainings",
    title: "Trainings",
    icon: <BookOpenText size={19} strokeWidth={2} />,
    children: [
      { route: "/training-programs",           title: "Training Programs",            icon: <BookOpen size={16} strokeWidth={2} />,   PermissionKey: "VIEW_TRAINING_PROGRAMS" },
      { route: "/program-monthly-traffic",     title: "Program Monthly Traffic",      icon: <BarChart3 size={16} strokeWidth={2} />, PermissionKey: "VIEW_PROGRAM_MONTHLY_TRAFFIC" },
      { route: "/training-programs-categories", title: "Training Program Categories",  icon: <FolderTree size={16} strokeWidth={2} />, PermissionKey: "VIEW_TRAINING_PROGRAM_CATEGORIES" },
      { route: "/training-program-modules",    title: "Training Program Modules",      icon: <Layers size={16} strokeWidth={2} />, PermissionKey: "VIEW_TRAINING_PROGRAM_MODULES" },
      { route: "/module-questions",            title: "Module Questions",              icon: <CircleHelp size={16} strokeWidth={2} />, PermissionKey: "VIEW_MODULE_QUESTIONS" },
    ],
  },
  {
    heading: "Certificates",
    title: "Certificates",
    route: "/certificates",
    icon: <Award size={19} strokeWidth={2} />,
    PermissionKey: "VIEW_CERTIFICATES",
  },
  {
    heading: "Placements",
    title: "Placements",
    icon: <Briefcase size={19} strokeWidth={2} />,
    children: [
      { route: "/recruitment-agencies",  title: "Recruitment Agencies",   icon: <Building2 size={16} strokeWidth={2} />,  PermissionKey: PLACEMENT_AGENCY_PERMISSIONS },
      { route: "/companies",             title: "Companies",               icon: <Building size={16} strokeWidth={2} />,   PermissionKey: PLACEMENT_COMPANY_PERMISSIONS },
      { route: "/placement-pool",        title: "Placement Pool",          icon: <UsersRound size={16} strokeWidth={2} />, PermissionKey: PLACEMENT_ANY_PERMISSIONS },
      { route: "/placement-referrals",   title: "Placement Referrals",     icon: <Handshake size={16} strokeWidth={2} />,  PermissionKey: PLACEMENT_REFERRAL_PERMISSIONS },
      { route: "/placement-assistance",  title: "Placement Assistance",    icon: <ClipboardCheck size={16} strokeWidth={2} />, PermissionKey: PLACEMENT_ASSISTANCE_PERMISSIONS },
      { route: "/placement-mock-interviews", title: "Mock Interviews",     icon: <MessageSquareText size={16} strokeWidth={2} />, PermissionKey: PLACEMENT_MOCK_INTERVIEW_PERMISSIONS },
    ],
  },
  {
    heading: "Reviews",
    title: "Reviews",
    icon: <MessageSquareText size={19} strokeWidth={2} />,
    children: [
      { route: "/student-review-platforms", title: "Review Platforms",  icon: <Star size={16} strokeWidth={2} />,              PermissionKey: "VIEW_REVIEW_PLATFORM" },
      { route: "/student-reviews",  title: "Student Reviews",   icon: <MessageSquareQuote size={16} strokeWidth={2} />, PermissionKey: "VIEW_STUDENT_REVIEW" },
    ],
  },
  {
    heading: "Setup",
    title: "Setup",
    icon: <SlidersHorizontal size={19} strokeWidth={2} />,
    children: [
      { route: "/lead-stages",                 title: "Lead Stages",                   icon: <ListChecks size={16} strokeWidth={2} />,  PermissionKey: "VIEW_LEAD_STAGE" },
      { route: "/lead-sources",                title: "Lead Sources",                  icon: <GitMerge size={16} strokeWidth={2} />,    PermissionKey: "VIEW_LEAD_SOURCE" },
      { route: "/training-spaces",             title: "Training Spaces",               icon: <DoorOpen size={16} strokeWidth={2} />,    PermissionKey: "VIEW_TRAINING_SPACES" },
      { route: "/slot-availability",           title: "Slot Availability",             icon: <CalendarCheck size={16} strokeWidth={2} />, PermissionKey: "VIEW_SLOT_AVALABILITY" },
      { route: "/trainer-slot-availability",   title: "Trainer Slot Availability",     icon: <CalendarClock size={16} strokeWidth={2} />, PermissionKey: "VIEW_TRAINER_SLOT_AVALABILITY" },
    ],
  },
  {
    heading: "Employee Management",
    title: "Employee Management",
    icon: <Users size={19} strokeWidth={2} />,
    children: [
      { route: "/organization/employee/employee-details", title: "Employees",       icon: <UserRound size={16} strokeWidth={2} />,  PermissionKey: "EMPLOYEE_VIEW_ALL" },
      { route: "/organization/employee/employee-exits",   title: "Employee Exits",  icon: <UserRoundX size={16} strokeWidth={2} />, PermissionKey: "EMPLOYEE_EXIT_VIEW_ALL" },
    ],
  },
  {
    heading: "Organization Setup",
    title: "Organization Setup",
    icon: <Building2 size={19} strokeWidth={2} />,
    children: [
      { route: "/followup-structures",         title: "Follow-up Structures",   icon: <ListChecks size={16} strokeWidth={2} />, PermissionKey: "VIEW_FOLLOWUP_STRUCTURES" },
      { route: "/organization/details",          title: "Organizations",          icon: <Building2 size={16} strokeWidth={2} />, PermissionKey: "ORGANIZATION_ALL" },
      { route: "/organization/profile",          title: "Organization Profile",   icon: <BadgeInfo size={16} strokeWidth={2} />,   PermissionKey: "ORGANIZATION_PROFILE_ALL" },
      { route: "/organization/entity",           title: "Organization Entities",  icon: <Network size={16} strokeWidth={2} />,   PermissionKey: "ENTITY_ALL" },
      { route: "/organization/location",         title: "Locations",              icon: <MapPin size={16} strokeWidth={2} />,    PermissionKey: "LOCATION_ALL" },
      { route: "/organization/application-access", title: "Application Access",  icon: <KeyRound size={16} strokeWidth={2} />,  PermissionKey: "VIEW_ORGANIZATION_APPLICATION" },
      { route: "/organization/departments",      title: "Departments",            icon: <Grid2x2 size={16} strokeWidth={2} />,   PermissionKey: "DEPARTMENT_ALL" },
      { route: "/organization/designation",      title: "Designations",           icon: <BadgeCheck size={16} strokeWidth={2} />, PermissionKey: "DESIGNATION_ALL" },
      { route: "/organization/work-shift",       title: "Workshifts",             icon: <CalendarClock size={16} strokeWidth={2} />, PermissionKey: "SHIFT_VIEW_ALL" },
      { route: "/organization/data-grid-settings", title: "Organization Datagrid Configuration", icon: <TableProperties size={16} strokeWidth={2} />, PermissionKey: "ORGANIZATION_ALL" },
      { route: "/billing", title: "Plans & Billing", icon: <CreditCard size={16} strokeWidth={2} />, PermissionKey: "BILLING_ALL" },
    ],
  },
  {
    heading: "User management",
    title: "User management",
    icon: <UserCog size={19} strokeWidth={2} />,
    children: [
      { route: "/users",                           title: "Users",                      icon: <UserRound size={16} strokeWidth={2} />,     PermissionKey: "USER_VIEW_ALL" },
      { route: "/application/user-roles",          title: "User Roles",               icon: <ShieldCheck size={16} strokeWidth={2} />,   PermissionKey: "USER_ROLE_ALL" },
      { route: "/application/user-role-permission", title: "User Role Permissions",   icon: <ShieldEllipsis size={16} strokeWidth={2} />, PermissionKey: "USER_ROLE_PERMISSION_ALL" },
      { route: "/application/user-permission",     title: "User Permission Overrides", icon: <KeySquare size={16} strokeWidth={2} />,    PermissionKey: "USER_PERMISSION_ALL" },
      { route: "/application/activity-logs",       title: "Activity Logs",              icon: <ScrollText size={16} strokeWidth={2} />, PermissionKey: "VIEW_ACTIVITY_LOGS" },
      { route: "/application/email-logs",          title: "Email Logs",                 icon: <Mails size={16} strokeWidth={2} />, PermissionKey: "VIEW_EMAIL_LOGS" },
    ],
  },
];

const STAFF_OPTIONS = BASE_OPTIONS;

const TRAINER_OPTIONS = [
  {
    heading: "Dashboard",
    title: "Dashboard",
    route: "/dashboard",
    icon: <LayoutDashboard size={19} strokeWidth={2} />,
  },
  {
    heading: "Batches",
    title: "Classes",
    icon: <Boxes size={19} strokeWidth={2} />,
    children: [
      { route: "/batches", title: "Batches", icon: <Boxes size={16} strokeWidth={2} />, PermissionKey: "VIEW_BATCHES||VIEW_SELF_BATCHES" },
      { route: "/batch-students", title: "Batch Students", icon: <Users size={16} strokeWidth={2} />, PermissionKey: "VIEW_BATCH_STUDENTS||VIEW_SELF_BATCH_STUDENTS" },
      { route: "/batch-classes", title: "Batch Classes", icon: <BookOpen size={16} strokeWidth={2} />, PermissionKey: "VIEW_BATCH_CLASS||VIEW_SELF_BATCH_CLASSES" },
      { route: "/class-records", title: "Class Records", icon: <ClipboardList size={16} strokeWidth={2} />, PermissionKey: "VIEW_BATCH_CLASS_RECORD||VIEW_SELF_BATCH_CLASS_RECORDS" },
      { route: "/classes-attendance", title: "Attendance", icon: <SquareCheckBig size={16} strokeWidth={2} />, PermissionKey: "VIEW_CLASS_ATTENDANCES||VIEW_SELF_CLASS_ATTENDANCE" },
      { route: "/attendance-report", title: "Attendance Report", icon: <FileBarChart2 size={16} strokeWidth={2} />, PermissionKey: "VIEW_CLASS_ATTENDANCES||VIEW_SELF_CLASS_ATTENDANCE" },
    ],
  },
  {
    heading: "Assesments",
    title: "Assessments",
    icon: <FileCheck size={19} strokeWidth={2} />,
    children: [
      { route: "/assesments", title: "Assessments", icon: <ClipboardPen size={16} strokeWidth={2} />, PermissionKey: "VIEW_ASSESSMENTS||VIEW_SELF_ASSESSMENTS" },
      { route: "/assessment-tests", title: "Tests", icon: <CircleHelp size={16} strokeWidth={2} />, PermissionKey: "VIEW_ASSESSMENT_TESTS||VIEW_SELF_ASSESSMENT_TESTS" },
      { route: "/assessment-schedules", title: "Schedules", icon: <CalendarClock size={16} strokeWidth={2} />, PermissionKey: "VIEW_ASSESSMENT_SCHEDULES||VIEW_SELF_ASSESSMENT_SCHEDULES" },
      { route: "/assesment-results", title: "Results", icon: <BarChart3 size={16} strokeWidth={2} />, PermissionKey: "VIEW_ASSESSMENT_RESULTS||VIEW_SELF_ASSESSMENT_RESULTS" },
    ],
  },
  {
    heading: "Trainings",
    title: "Materials",
    icon: <BookOpenText size={19} strokeWidth={2} />,
    children: [
      { route: "/training-program-modules", title: "Program Modules", icon: <Layers size={16} strokeWidth={2} />, PermissionKey: "VIEW_TRAINING_PROGRAM_MODULES||VIEW_SELF_TRAINING_PROGRAM_MODULES" },
      { route: "/module-questions", title: "Module Questions", icon: <CircleHelp size={16} strokeWidth={2} />, PermissionKey: "VIEW_MODULE_QUESTIONS||VIEW_SELF_MODULE_QUESTIONS" },
    ],
  },
  {
    heading: "Leads",
    title: "Demos",
    icon: <MonitorPlay size={19} strokeWidth={2} />,
    children: [
      { route: "/demo-sessions", title: "Demo Sessions", icon: <MonitorPlay size={16} strokeWidth={2} />, PermissionKey: "VIEW_DEMO_SESSIONS||VIEW_SELF_DEMO_SESSIONS" },
      { route: "/demo-participants", title: "Demo Participants", icon: <UserCheck size={16} strokeWidth={2} />, PermissionKey: "VIEW_DEMO_PARTICIPANTS||VIEW_SELF_DEMO_PARTICIPANTS" },
    ],
  },
];

/* ── Animated collapse ───────────────────────── */
function NavChildren({ open, children }) {
  const ref = useRef(null);
  const [h, setH] = useState(open ? "auto" : 0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open) {
      const target = el.scrollHeight;
      setH(target);
      const t = setTimeout(() => setH("auto"), 230);
      return () => clearTimeout(t);
    } else {
      setH(el.scrollHeight);
      requestAnimationFrame(() => requestAnimationFrame(() => setH(0)));
    }
  }, [open]);
  return (
    <div
      className="ems-nav-children"
      ref={ref}
      style={{ height: h === "auto" ? "auto" : h + "px" }}
    >
      <div style={{ paddingBottom: 4 }}>{children}</div>
    </div>
  );
}

/* ── SideBar ─────────────────────────────────── */
export default function SideBar({ Permission, collapsed, onRequestExpand, workspace }) {
  const { logout, userData } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const activeWorkspace =
    workspace || resolveActiveWorkspace(userData, Permission);
  const studentMode = activeWorkspace === WORKSPACES.STUDENT;
  const trainerMode = activeWorkspace === WORKSPACES.TRAINER;
  const sourceOptions = studentMode
    ? STUDENT_OPTIONS
    : trainerMode
      ? TRAINER_OPTIONS
      : STAFF_OPTIONS;
  const [openGroups, setOpenGroups] = useState(() => {
    const o = {};
    sourceOptions.forEach((item) => {
      if (item.children && item.children.some((c) => c.route === location.pathname)) {
        o[item.heading] = true;
      }
    });
    return o;
  });

  useEffect(() => {
    const o = {};
    sourceOptions.forEach((item) => {
      if (item.children && item.children.some((c) => c.route === location.pathname)) {
        o[item.heading] = true;
      }
    });
    setOpenGroups(o);
  }, [activeWorkspace]);

  const hasPermission = (action) => {
    if (!Permission || !Array.isArray(Permission) || !action) return false;
    const tokens = String(action)
      .split("||")
      .map((token) => String(token).trim().toUpperCase())
      .filter(Boolean);
    if (!tokens.length) return false;
    return tokens.some((token) =>
      Permission.some((p) => String(p).trim().toUpperCase() === token)
    );
  };

  const sidebaroption = useMemo(() => {
    const options = studentMode
      ? STUDENT_OPTIONS
      : trainerMode
        ? TRAINER_OPTIONS
        : STAFF_OPTIONS;
    if (!Permission || !Array.isArray(Permission)) {
      return options.filter((item) => {
        if (item.children) {
          return item.children.some((child) => !child.PermissionKey);
        }
        return !item.PermissionKey;
      });
    }

    if (studentMode || trainerMode) {
      const filtered = options
        .map((item) => {
          if (item.children) {
            const allowedChildren = item.children.filter((child) =>
              child.PermissionKey ? hasPermission(child.PermissionKey) : true
            );
            if (allowedChildren.length === 0) return null;
            return { ...item, children: allowedChildren };
          }
          return item.PermissionKey && !hasPermission(item.PermissionKey)
            ? null
            : item;
        })
        .filter(Boolean);
      return filtered.length ? filtered : options;
    }

    return options.map((item) => {
      if (item.children) {
        const allowedChildren = item.children.filter((child) => {
          if (child.route === "/demo-sessions")
            return hasPermission("VIEW_DEMO_SESSIONS") || hasPermission("VIEW_SELF_DEMO_SESSIONS");
          if (child.route === "/demo-participants")
            return hasPermission("VIEW_DEMO_PARTICIPANTS") || hasPermission("VIEW_SELF_DEMO_PARTICIPANTS");
          if (TRAINER_PORTAL_ROUTES.includes(child.route) && trainerMode) return true;
          if (child.route === "/batches")
            return hasPermission("VIEW_BATCHES") || hasPermission("VIEW_SELF_BATCHES");
          if (child.route === "/batch-students")
            return hasPermission("VIEW_BATCH_STUDENTS") || hasPermission("VIEW_SELF_BATCH_STUDENTS");
          if (child.route === "/batch-classes")
            return hasPermission("VIEW_BATCH_CLASS") || hasPermission("VIEW_SELF_BATCH_CLASSES");
          if (child.route === "/class-records")
            return hasPermission("VIEW_BATCH_CLASS_RECORD") || hasPermission("VIEW_SELF_BATCH_CLASS_RECORDS");
          if (child.route === "/classes-attendance")
            return hasPermission("VIEW_CLASS_ATTENDANCES") || hasPermission("VIEW_SELF_CLASS_ATTENDANCE");
          if (child.route === "/attendance-report")
            return hasPermission("VIEW_CLASS_ATTENDANCES") || hasPermission("VIEW_SELF_CLASS_ATTENDANCE");
          if (child.route === "/organization/details" || child.route === "/organization/switcher")
            return canAccessOrganizationList(Permission);
          if (child.route === "/followup-structures")
            return hasPermission("VIEW_FOLLOWUP_STRUCTURES");
          return child.PermissionKey ? hasPermission(child.PermissionKey) : true;
        });
        if (allowedChildren.length === 0) return null;
        return { ...item, children: allowedChildren };
      }
      if (item.PermissionKey) return hasPermission(item.PermissionKey) ? item : null;
      return item;
    }).filter(Boolean);
  }, [Permission, studentMode, trainerMode]);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0891B2",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");
          await axios.get(`${MAIN_URL}/api/auth/logout`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const loginPath = getLoginPathAfterLogout(userData);
          logout();
          localStorage.removeItem("token");
          navigate(loginPath);
        } catch {
          const loginPath = getLoginPathAfterLogout(userData);
          logout();
          navigate(loginPath);
        }
      }
    });
  };

  const handleNav = (route) => navigate(route);

  const handleParentNavClick = (heading) => {
    if (collapsed) {
      onRequestExpand?.();
      setOpenGroups((o) => ({ ...o, [heading]: true }));
      return;
    }
    toggleGroup(heading);
  };

  const handleLeafNavClick = (route) => {
    if (collapsed) {
      onRequestExpand?.();
    }
    handleNav(route);
  };

  const toggleGroup = (heading) =>
    setOpenGroups((o) => ({ ...o, [heading]: !o[heading] }));

  let seenLabels = new Set();

  return (
    <>
      <div className="ems-sidebar-scroll">
        {sidebaroption.map((item, index) => {
          const sectionLabel = SECTION_BEFORE[item.heading];
          const showLabel = sectionLabel && !seenLabels.has(sectionLabel);
          if (showLabel) seenLabels.add(sectionLabel);

          const isActive = !item.children && item.route === location.pathname;
          const childActive = item.children && item.children.some((c) => c.route === location.pathname);
          const isOpen = !!openGroups[item.heading];

          return (
            <React.Fragment key={item.heading || index}>
              {showLabel && (
                <div className="ems-nav-section-label">{sectionLabel}</div>
              )}
              {item.children ? (
                <>
                  <button
                    className={[
                      "ems-nav-item",
                      childActive ? "parent-active" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => handleParentNavClick(item.heading)}
                    title={collapsed ? item.title : undefined}
                  >
                    <span className="ems-ni-icon">{item.icon}</span>
                    <span className="ems-ni-label">{item.title}</span>
                    <span className={`ems-ni-chev${isOpen ? " open" : ""}`}>
                      <ChevronRight size={16} strokeWidth={2.4} />
                    </span>
                  </button>
                  {!collapsed && (
                    <NavChildren open={isOpen}>
                      {item.children.map((child) => (
                        <button
                          key={child.route}
                          className={[
                            "ems-nav-child",
                            location.pathname === child.route ? "active" : "",
                          ].filter(Boolean).join(" ")}
                          onClick={() => handleLeafNavClick(child.route)}
                        >
                          <span className="ems-nc-icon">{child.icon}</span>
                          <span className="ems-nc-label">{child.title}</span>
                        </button>
                      ))}
                    </NavChildren>
                  )}
                </>
              ) : (
                <button
                  className={[
                    "ems-nav-item",
                    isActive ? "active" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => handleLeafNavClick(item.route)}
                  title={collapsed ? item.title : undefined}
                >
                  <span className="ems-ni-icon">{item.icon}</span>
                  <span className="ems-ni-label">{item.title}</span>
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="ems-sidebar-foot">
        <button
          className="ems-nav-item"
          onClick={handleLogout}
          title={collapsed ? "Logout" : undefined}
        >
          <span className="ems-ni-icon">
            <LogOut size={19} strokeWidth={2} />
          </span>
          <span className="ems-ni-label">Logout</span>
        </button>
      </div>
    </>
  );
}
